import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AppleLogo, RayMark } from '@/components/landing/brand-marks'

import { DOWNLOAD_URL, NAV_LINKS, SIGN_IN_URL } from '@/constants/navigation'

const SiteHeader: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (!open) return

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        const handleResize = () => {
            if (window.innerWidth >= 1024) setOpen(false)
        }

        window.addEventListener('keydown', handleKey)
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('keydown', handleKey)
            window.removeEventListener('resize', handleResize)
        }
    }, [open])

    return (
        <header className='fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-4 sm:pt-4'>
            <div className='relative mx-auto max-w-[1204px]'>
                <nav
                    aria-label='Main'
                    className='flex h-[60px] items-center justify-between gap-4 rounded-2xl border border-white/[0.07] bg-[#0a0b0d]/80 px-4 shadow-[0_10px_40px_rgb(0_0_0/0.45)] backdrop-blur-xl sm:h-[75px] sm:px-8'
                >
                    <Link
                        href='/'
                        className='flex shrink-0 items-center gap-2.5 rounded-md text-[17px] font-semibold tracking-[-0.01em] text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/60'
                    >
                        <RayMark className='size-[22px] text-brand' />
                        Raycast
                    </Link>

                    <ul className='hidden items-center gap-8 lg:flex'>
                        {NAV_LINKS.map(link => (
                            <li key={link.label}>
                                <a
                                    href={link.href}
                                    className='rounded-sm text-[15px] text-[#9c9ca0] transition-colors hover:text-white focus-visible:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/50'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className='flex shrink-0 items-center gap-2 sm:gap-6'>
                        <a
                            href={SIGN_IN_URL}
                            className='hidden rounded-sm text-[15px] text-[#9c9ca0] transition-colors hover:text-white focus-visible:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/50 sm:inline'
                        >
                            Log in
                        </a>

                        <a
                            href={DOWNLOAD_URL}
                            className='inline-flex h-9 items-center gap-2 rounded-[10px] bg-gradient-to-b from-[#f7f7f8] to-[#d9d9dc] px-3 text-[14px] font-medium text-[#111113] shadow-[inset_0_1px_0_rgb(255_255_255),0_1px_3px_rgb(0_0_0/0.5)] transition-[filter,transform] hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white active:translate-y-px sm:h-[38px] sm:text-[15px]'
                        >
                            <AppleLogo className='size-4' />
                            Download
                        </a>

                        <button
                            type='button'
                            onClick={() => setOpen(value => !value)}
                            aria-expanded={open}
                            aria-controls='mobile-menu'
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            className='grid size-9 place-items-center rounded-lg text-[#c4c4c8] transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline-2 focus-visible:outline-white/60 lg:hidden'
                        >
                            {open ? <X className='size-5' /> : <Menu className='size-5' />}
                        </button>
                    </div>
                </nav>

                <div
                    id='mobile-menu'
                    hidden={!open}
                    className={cn(
                        'absolute inset-x-0 top-[calc(100%_+_8px)] rounded-2xl border border-white/[0.07] bg-[#0a0b0d]/95 p-2 shadow-[0_20px_60px_rgb(0_0_0/0.6)] backdrop-blur-xl lg:hidden',
                        open && 'animate-in fade-in slide-in-from-top-2 duration-200'
                    )}
                >
                    <ul className='grid grid-cols-2 gap-1 sm:grid-cols-3'>
                        {NAV_LINKS.map(link => (
                            <li key={link.label}>
                                <a
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className='flex h-11 items-center rounded-xl px-3 text-[15px] text-[#c4c4c8] transition-colors hover:bg-white/[0.05] hover:text-white'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>

                    <a
                        href={SIGN_IN_URL}
                        className='mt-2 flex h-11 items-center justify-center rounded-xl border border-white/10 text-[15px] font-medium text-white transition-colors hover:bg-white/[0.05] sm:hidden'
                    >
                        Log in
                    </a>
                </div>
            </div>
        </header>
    )
}

export default SiteHeader
